// Iter 31.149 — Shared telemetry grid (30 May 2026).
//
// Canonical 4-cell health surface that both /chamber and /diagnostics
// consume. Source of truth for: what diagnostics fields render where,
// how health flips, what fix-action endpoint each cell is wired to.
//
// Both surfaces pass in the live telemetry payload (from GET
// /api/diagnostics/health) and a `runAction` callback. The grid owns
// the cell layout, healthy-flag computation, and action-prop wiring.
// Pages own the wrapping chrome + the diagnostics-fetch lifecycle.
import React from "react";
import { Wrench } from "lucide-react";

export function TelemetryCell({ testid, title, bearing, rows, healthy, action }) {
    return (
        <div
            data-testid={testid}
            className={`relative paper-card bg-black/70 border-2 p-4 flex flex-col gap-2 ${healthy ? "border-amber-400/70 shadow-[0_0_30px_rgba(251,191,36,0.25)]" : "border-red-500/60 shadow-[0_0_30px_rgba(239,68,68,0.25)]"}`}
        >
            <div className="flex items-center justify-between gap-2">
                <div className="text-[10px] tracking-[0.3em] text-amber-300/80 font-mono">{title}</div>
                <div className="text-[9px] tracking-[0.2em] text-amber-300/40 font-mono">{bearing}</div>
            </div>
            <div className="flex-1 space-y-1.5 font-mono">
                {rows.map(([k, v], i) => (
                    <div key={i} className="flex justify-between items-baseline gap-3 text-sm border-b border-amber-900/30 pb-1 last:border-0">
                        <span className="text-amber-300/60 text-[11px] tracking-wide">{k}</span>
                        <span className="text-amber-100 text-right max-w-[60%] break-all">
                            {v === null || v === undefined || v === "" ? <span className="opacity-30">—</span> : String(v)}
                        </span>
                    </div>
                ))}
            </div>
            {action && (
                <div className="mt-2 pt-2 border-t border-dashed border-amber-900/40 flex items-center justify-between gap-2">
                    <div data-testid={`${testid}-action-result`} className="text-[10px] tracking-wide text-amber-300/60 font-mono truncate">
                        {action.result || action.idleHint}
                    </div>
                    <button
                        data-testid={`${testid}-action-btn`}
                        disabled={action.disabled}
                        onClick={action.onClick}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[10px] tracking-[0.2em] border border-amber-700/60 hover:border-amber-300 disabled:opacity-30 disabled:cursor-not-allowed text-amber-200 whitespace-nowrap"
                    >
                        <Wrench size={10} className={action.busy ? "animate-spin" : ""} />
                        {action.busy ? "RUNNING…" : action.label}
                    </button>
                </div>
            )}
            <div className={`absolute top-2 right-2 w-1.5 h-1.5 rounded-full ${healthy ? "bg-amber-300" : "bg-red-500"} shadow-[0_0_8px_currentColor]`} />
        </div>
    );
}

// Render-the-grid helper. Both pages call this with the same args.
// `actionBusy` and `actionResult` are the iter 31.144 state slots
// (string + dict). `runAction(name, endpoint, formatResult)` is the
// shared dispatcher.
export function TelemetryGrid({ telemetry, actionBusy, actionResult, runAction }) {
    const tc = telemetry?.two_channel;
    const cw = telemetry?.credit_wall;
    const dw = telemetry?.drive_watch;
    const sfm = telemetry?.spy_finance_mirror;

    return (
        <>
            <TelemetryCell
                testid="telemetry-two-channel"
                title="// VILLAGE · TWO-CHANNEL"
                bearing="EAST WALL"
                rows={[
                    ["Loop", tc?.loop_running ? "RUNNING" : "DOWN"],
                    ["Static file", tc?.static_file_exists ? "present" : "missing"],
                    ["Last detector", tc?.last_activity
                        ? `${tc.last_activity.active ? "ACTIVE" : "idle"} · ${tc.last_activity.triggered_by || "—"}`
                        : "—"],
                ]}
                healthy={tc?.loop_running}
                action={{
                    label: "FORCE REFRESH",
                    busy: actionBusy === "two-channel-refresh",
                    disabled: actionBusy === "two-channel-refresh",
                    result: actionResult["two-channel-refresh"],
                    idleHint: "Writes /two-channel.json now.",
                    onClick: () => runAction(
                        "two-channel-refresh",
                        "/comms/two-channel/refresh",
                        (out) => out?.static_bypass
                            ? `Wrote static at ${(out.synced_at || "").replace("T", " ").slice(0, 19)}`
                            : `OK · ${out?.url_hint || "channel synced"}`,
                    ),
                }}
            />
            <TelemetryCell
                testid="telemetry-mirror"
                title="// REGIONAL · SPY-FINANCE MIRROR"
                bearing="EAST + SOUTH"
                rows={[
                    ["Credit ledger", sfm?.credit_ledger_rows],
                    ["Mirror rows", sfm?.mirror_rows],
                    ["Lag", sfm?.mirror_lag_rows],
                ]}
                healthy={sfm?.mirror_healthy}
                action={{
                    label: "RUN BACKFILL",
                    busy: actionBusy === "mirror-backfill",
                    disabled: actionBusy === "mirror-backfill" || sfm?.mirror_healthy,
                    result: actionResult["mirror-backfill"],
                    idleHint: sfm?.mirror_healthy
                        ? "Nothing to do."
                        : "Backfill will catch up the paired ledger.",
                    onClick: () => runAction(
                        "mirror-backfill",
                        "/credits/spyfinance-backfill",
                        (out) => `Scanned ${out.scanned}, mirrored ${out.mirrored}, skipped ${out.skipped_already_mirrored}, errors ${out.errors?.length || 0}`,
                    ),
                }}
            />
            <TelemetryCell
                testid="telemetry-credit-wall"
                title="// BRIEF METRICS · CREDIT WALL · 30D"
                bearing="SOUTH WALL"
                rows={[
                    ["Top-ups", cw?.topup_count],
                    ["Sub starts", cw?.subscription_starts],
                    ["Credits debited", cw?.debit_credits_total],
                ]}
                healthy={(cw?.topup_count || 0) + (cw?.subscription_starts || 0) > 0}
            />
            <TelemetryCell
                testid="telemetry-drive"
                title="// COMMAND INTERFACE · DRIVE WATCH"
                bearing="WEST + NORTH"
                rows={[
                    ["Account", dw?.google_email],
                    ["Folder", dw?.watched_folder_name],
                    ["Last poll", dw?.last_poll_at?.replace("T", " ").slice(0, 19)],
                    ["Auto-poll", dw?.polling_enabled ? `on · ${dw?.poll_interval_minutes}m` : "off · manual"],
                ]}
                healthy={dw?.connected}
            />
        </>
    );
}
